import type {
  DeliveryTableSeriesKey,
  SeriesKey,
  SeriesMeta,
} from '../../api/types.js';

/**
 * Every row a chart card can show: the four drawn series plus the table-only rows Delivery adds
 * (FR-D26).
 *
 * The distinction lives in the response — a series marked `display: 'table'` gets a matrix row
 * and no line — so this union is the *domain* of the labels below, not a statement about which
 * rows any one chart has.
 */
export type ChartRowKey = SeriesKey | DeliveryTableSeriesKey;
import { color } from '../../theme/tokens.js';

/**
 * What the plot and the table both have to agree about.
 *
 * `<LineChart>` and `<ChartTable>` are two readings of one response (owner request 2026-09-09),
 * and everything they must not answer differently lives here: the wire shape, which series exist
 * and what they are called, and the window each bucket covers. Two copies of `SERIES_FULL` is how
 * a column header comes to name a series the legend calls something else; two copies of
 * `formatBucketWindow` is how a matrix column comes to describe a different window from the hover
 * bubble over the same bucket.
 *
 * It is a module rather than props on `<ChartTable>` because the table is rendered *by* the
 * chart: passing the maps down would put the definitions in the parent and make the child's
 * headers a function of who called it.
 */

/**
 * Bucket-over-bucket change, in percent — the comparison the summary strip makes, and **the strip
 * alone** since 2026-09-10.
 *
 * ⚠ **This is NOT Region Performance's arrow, and the two are one screen apart.** Every trend
 * arrow in that table is a fixed 30-day trailing comparison that never moves with the period
 * (FR-054); this one is *this bucket against the one before it*, so it spans one day at a daily
 * grain, seven days at a weekly one and three months at a quarterly one. That is why the strip
 * names both ends of its own comparison on its face (`Aug 2026 · vs Jul 2026`) — an unlabelled
 * figure that moves when a chart control is touched is exactly what FR-034 is about, and it is
 * also why `<ChartTable>` no longer prints this at all: a matrix cell has no room to name its
 * window, and the columns already are the comparison. ⚠ Nothing here may be rewired to
 * `Metric.d30`, and nothing may put it back in a table cell: same glyph, three different windows.
 *
 * `null` rather than `0` wherever the change is not a number (FR-055): the first bucket has
 * nothing before it, and a previous value of `0` has no percentage at all — a rise from 0 to 40
 * is not `+4000%`, it is undefined, and printing anything there invents a figure.
 */
export function bucketChange(
  current: number | null,
  previous: number | null | undefined,
): number | null {
  if (current === null || previous === null || previous === undefined) return null;
  if (!Number.isFinite(current) || !Number.isFinite(previous) || previous === 0) return null;
  return ((current - previous) / Math.abs(previous)) * 100;
}

/**
 * How one change reads — the glyph, the figure, its colour, and the words for a reader who gets
 * none of the three.
 *
 * The glyph is decided by the **rounded** figure, not the raw one, so the arrow can never
 * disagree with the number printed beside it: `+0.02%` prints as `0.0%`, and a green rise beside
 * a figure that says nothing changed is the sort of contradiction nothing on screen would catch.
 * No glyph at no change, exactly as `<MetricCell>` draws none — an arrow asserts a direction.
 */
export function changeFace(change: number): {
  glyph: string;
  text: string;
  tone: string;
  spoken: string;
} {
  const rounded = Math.round(change * 10) / 10;
  const text = `${Math.abs(rounded).toFixed(1)}%`;
  if (rounded === 0) return { glyph: '', text, tone: color.inkSubtle, spoken: 'no change' };
  return rounded > 0
    ? { glyph: '▲', text, tone: color.positive, spoken: `up ${text}` }
    : { glyph: '▼', text, tone: color.negative, spoken: `down ${text}` };
}

/**
 * Render order. Which of the four are actually drawn is decided by the **`series` map the API
 * sent**, not by this list.
 *
 * That is what lets the Delivery chart carry three lines with no gap and no empty legend slot: a
 * job has no recruiter, so it cannot be scoped to a person (FR-D09), and its timeseries response
 * simply has no `jobs` key. Hardcoding four here would render an always-empty fourth line on
 * every Delivery scope.
 */
export const SERIES_ORDER: SeriesKey[] = ['toa', 'jobs', 'subs', 'offers'];

/**
 * Every row the matrix may draw, in order: the four drawn series, then the table-only ones
 * (FR-D26).
 *
 * Two lists rather than one, and the split is a type as much as an order — the plot's domain is
 * `SeriesKey` and always was, so a key that can never be a line cannot reach `scaleSeries`. The
 * table-only rows come **after** the drawn ones so a reader switching views finds the rows they
 * were just looking at where they left them, with the new ones below rather than interleaved.
 *
 * ⚠ Neither list decides what a chart *has*: that is the `series` map the API sent. These decide
 * the order of whatever it sent.
 */
export const ROW_ORDER: ChartRowKey[] = [
  ...SERIES_ORDER,
  // The run-rate rows in lifecycle order — never started, started, ended — then the Nexus one.
  'backouts',
  'starts',
  'terms',
  'uniqueSubs',
];

export const SERIES_LABEL: Record<ChartRowKey, string> = {
  toa: 'TOA',
  jobs: 'JOBS',
  subs: 'SUBS',
  offers: 'OFFERS',
  backouts: 'BACKOUTS',
  starts: 'STRTS',
  terms: 'TERM',
  uniqueSubs: 'UNIQ SUBS',
};

export const SERIES_FULL: Record<ChartRowKey, string> = {
  toa: 'Active TOA — Travelers on Assignment',
  jobs: 'Job Openings',
  subs: 'Submissions',
  offers: 'Offers',
  backouts: 'Backouts — placements that did not start or did not accept',
  starts: 'Starts — assignments that began in the bucket',
  terms: 'Terminations — assignments that ended in the bucket',
  uniqueSubs: 'Unique Submissions — distinct candidates submitted',
};

/**
 * Rows the hover panel must **not** decompose (FR-084).
 *
 * Every other figure here splits cleanly into Internal · External · Target · Nursing · Allied.
 * `uniqueSubs` does not: a candidate submitted to both a Nursing and an Allied job in the bucket
 * is one distinct candidate and counts in *both* halves, so the parts overshoot the whole — 4
 * over at 90d, 10 at 12M, 30 at 24M on the region table's own measurement. Parts that exceed
 * their total read as an arithmetic fault, which is why Region Performance decomposes `subs`
 * alone in the same situation. The cell keeps its figure, its exact value and its provenance; it
 * loses only the breakdown it cannot honestly give.
 */
export const NO_DECOMPOSITION: ReadonlySet<ChartRowKey> = new Set<ChartRowKey>(['uniqueSubs']);

/**
 * The least common denominator of the region and Delivery timeseries responses.
 *
 * Structural rather than a union of the two wire types, so a third scoped chart needs no edit
 * here. `jobs` is optional on the point and every entry of `series` is optional — the presence of
 * a key in `series` is what declares a line exists.
 */
export interface ChartPoint {
  key: string;
  label: string;
  isPartial: boolean;
  /**
   * The UTC window the bucket covers, ISO-8601 — see `formatBucketWindow`.
   *
   * Optional because the four `breakdown` sets are served without it: they are read for a figure
   * at an already-hovered index and never name a bucket of their own.
   */
  start?: string;
  end?: string;
  toa: number | null;
  jobs?: number | null;
  subs: number | null;
  offers: number | null;
  /**
   * The table-only figures (FR-D26) — optional twice over: absent from the region responses
   * entirely, and absent from a Delivery snapshot promoted before 2026-09-11. A row whose figure
   * is missing renders `—`, never 0 (FR-055).
   */
  backouts?: number | null;
  starts?: number | null;
  terms?: number | null;
  uniqueSubs?: number | null;
}

export interface ChartData {
  points: ChartPoint[];
  breakdown?: {
    internal: ChartPoint[];
    target: ChartPoint[];
    nursing: ChartPoint[];
    allied: ChartPoint[];
  };
  series: Partial<Record<ChartRowKey, SeriesMeta>>;
}

/**
 * The window a bucket actually covers, in words (owner request 2026-09-10, FR-087).
 *
 * A bucket's label is an **identity**, not a window: `Q3-26` does not say whether the figure
 * covers July through September or July through the 10th of September, and `08/30` does not say
 * that the week behind it began on the 24th. The plot's hover bubble and the matrix's column
 * headers both have to answer that, and they have to answer it the same way — which is why the
 * formatter lives here beside `SERIES_FULL` and `bucketChange` rather than in either component.
 *
 * ⚠ **Everything here is UTC, deliberately, and the zone is named wherever a clock time is.**
 * The buckets are UTC business windows (`domain/periods.ts` touches no local accessor), so
 * rendering the start in UTC and the end in the reader's zone would compose one coherent-looking
 * window that never existed — the same trap `formatSyncedAt` documents in the header, arriving on
 * a range instead of an instant. The header still answers "when did the job run" in the reader's
 * own zone, because that is a wall-clock question about their morning; "what does this figure
 * cover" is not.
 *
 * ⚠ **`syncedAt` replaces the end on a PARTIAL bucket, and that is the point of the parameter.**
 * The served `end` is the bucket's query boundary — the end of the as-of day — while the rows
 * only run to the moment the job read BigQuery. On the daily grain those differ by most of a
 * day, which is exactly the comparability gap FR-085's dashed tail exists to flag; this is the
 * bubble finally saying what the dash means. It is ignored on a complete bucket, whose calendar
 * end really is where its rows stop.
 *
 * A `syncedAt` outside the bucket falls back to `end` rather than printing a window that runs
 * backwards: a degraded snapshot keeps the `promotedAt` of its last good promotion, so the two
 * clocks are not guaranteed to sit inside one another.
 *
 * `null` whenever the window is not on the wire — the breakdown series carry none, and a
 * snapshot read against a moved `asOf` loses the fields rather than its figures. The caller
 * renders the label alone in that case; the window is missing, the bucket is not.
 */
export function formatBucketWindow(
  point: Pick<ChartPoint, 'start' | 'end' | 'isPartial'>,
  syncedAt?: string | null,
): string | null {
  if (!point.start || !point.end) return null;

  const start = new Date(point.start);
  const bucketEnd = new Date(point.end);
  if (Number.isNaN(start.getTime()) || Number.isNaN(bucketEnd.getTime())) return null;

  const end = partialEnd(start, bucketEnd, point.isPartial, syncedAt);
  const sameDay = utcDay(start) === utcDay(end);

  if (sameDay) {
    // One day, so the dates would repeat — the clock times are the whole content.
    return `${dateFace(start, true)} · ${timeFace(start)} – ${timeFace(end)} UTC`;
  }

  const spanned = `${dateFace(start, start.getUTCFullYear() !== end.getUTCFullYear())} – ${dateFace(
    end,
    true,
  )}`;

  // The clock time is only worth the width where it says something a date cannot: that the
  // newest bucket stops partway through its last day.
  return point.isPartial && end.getTime() !== bucketEnd.getTime()
    ? `${spanned} · through ${timeFace(end)} UTC`
    : spanned;
}

/**
 * Where the rows actually stop.
 *
 * Guarded both ways: a `syncedAt` past the bucket's own end would claim coverage the query never
 * had, and one before its start would print a backwards range.
 */
function partialEnd(
  start: Date,
  bucketEnd: Date,
  isPartial: boolean,
  syncedAt: string | null | undefined,
): Date {
  if (!isPartial || !syncedAt) return bucketEnd;
  const at = new Date(syncedAt);
  if (Number.isNaN(at.getTime())) return bucketEnd;
  if (at.getTime() < start.getTime() || at.getTime() > bucketEnd.getTime()) return bucketEnd;
  return at;
}

function utcDay(d: Date): string {
  return d.toISOString().slice(0, 10);
}

function dateFace(d: Date, withYear: boolean): string {
  return d.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    ...(withYear ? { year: 'numeric' } : {}),
    timeZone: 'UTC',
  });
}

function timeFace(d: Date): string {
  return d.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    timeZone: 'UTC',
  });
}
